import React, { useState, useEffect, useMemo } from "react";
import { UserCheck, RefreshCw, ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import clientsService from "../../../api/ClientsService";
import Loader from "../../../components/misc/Loader";
import ClientStatsCards from "./registered-clients/ClientStatsCards";
import ClientFilters from "./registered-clients/ClientFilters";
import ClientTable from "./registered-clients/ClientTable";
import ClientCard from "./registered-clients/ClientCard";
import ClientViewModal from "./registered-clients/ClientViewModal";
import ClientEditModal from "./registered-clients/ClientEditModal";
import { useSearchParams, useNavigate } from "react-router-dom";
// import ClientActivateModal from "./registered-clients/ClientActivateModal"; // COMMENTED OUT

const RegisteredClients = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const agentSlug = searchParams.get("agent");
  const [agentInfo, setAgentInfo] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // View details modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Activation/Deactivation modal state - COMMENTED OUT
  // const [showActivateModal, setShowActivateModal] = useState(false);
  // const [clientToActivate, setClientToActivate] = useState(null);
  // const [activating, setActivating] = useState(false);
  // const [isDeactivating, setIsDeactivating] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [clientToEdit, setClientToEdit] = useState(null);

  // Format document type for display
  const formatDocumentType = (docType) => {
    if (!docType) return "N/A";
    return docType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper to normalize date strings (handles ISO format, YYYY-MM-DD, etc.)
  const normalizeDateString = (dateStr) => {
    if (!dateStr) return dateStr;
    // If it's an ISO string like "2026-01-23T00:00:00.000000Z", extract date part
    if (typeof dateStr === 'string' && dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    // If already in YYYY-MM-DD format, return as is
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    return dateStr;
  };

  // Map API response to consistent format
  const mapClientFromApi = (c) => {
    if (!c) return c;
    
    // Determine status based on verification
    // Active if either email or phone is verified
    // Pending if neither is verified
    let status = "Pending";
    if (c.email_verified_at || c.phone_number_verified_at) {
      status = "Active";
    }
    
    // Extract agent information
    const agent = c.agent || null;
    const agentCode = agent?.agent_code || null;
    const agentName = agent 
      ? `${agent.first_name || ""} ${agent.last_name || ""}`.trim() 
      : "";
    const agentEmail = agent?.email || "";
    const agentPhone = agent?.phone_number || "";
    const agentSlug = agent?.slug || c.agent_slug || null;
    const agentType = agent?.agent_type || null;
    const agentStatus = agent?.status !== undefined ? agent.status : null;
    
    return {
      ...c,
      firstName: c.first_name || c.firstName,
      lastName: c.last_name || c.lastName,
      email: c.email,
      phone: c.phone_number || c.phone,
      region: c.region,
      location: c.location,
      documentType: formatDocumentType(c.document_type),
      cardNumber: c.card_number || c.cardNumber,
      issueDate: normalizeDateString(c.issue_date || c.issueDate),
      expiryDate: normalizeDateString(c.expiry_date || c.expiryDate),
      idCardFront: c.id_card_front || c.idCardFront,
      idCardBack: c.id_card_back || c.idCardBack,
      emailVerifiedAt: c.email_verified_at,
      phoneVerifiedAt: c.phone_number_verified_at,
      channel: c.channel || "web",
      // Agent information
      agent: agent,
      agentCode: agentCode,
      agentName: agentName,
      agentEmail: agentEmail,
      agentPhone: agentPhone,
      agentSlug: agentSlug,
      agentType: agentType,
      agentStatus: agentStatus,
      agentLocation: agent?.location || null,
      agentRegion: agent?.region || null,
      dob: c.dob || null,
      gender: c.gender || null,
      status,
      slug: c.slug || c.id,
    };
  };

  // Filter and search clients
  const filteredClients = useMemo(() => {
    return clients
      .filter((client) => {
        const matchesSearch =
          client.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.phone?.includes(searchTerm) ||
          client.region?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.documentType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.cardNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.agentCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.agentEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.agentPhone?.includes(searchTerm);

        const matchesStatus =
          statusFilter === "all" ||
          client.status?.toLowerCase() === statusFilter.toLowerCase();

        const matchesRegion =
          regionFilter === "all" ||
          client.region?.toLowerCase().includes(regionFilter.toLowerCase());

        return matchesSearch && matchesStatus && matchesRegion;
      })
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "name":
            aValue = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
            bValue = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
            break;
          case "email":
            aValue = (a.email || "").toLowerCase();
            bValue = (b.email || "").toLowerCase();
            break;
          case "location":
            aValue = (a.location || "").toLowerCase();
            bValue = (b.location || "").toLowerCase();
            break;
          case "document":
            aValue = (a.documentType || "").toLowerCase();
            bValue = (b.documentType || "").toLowerCase();
            break;
          case "status":
            aValue = a.status?.toLowerCase() || "";
            bValue = b.status?.toLowerCase() || "";
            break;
          default:
            aValue = a[sortBy] || "";
            bValue = b[sortBy] || "";
        }

        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
  }, [clients, searchTerm, statusFilter, regionFilter, sortBy, sortOrder]);

  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        let response;
        let clientsArray = [];

        if (agentSlug) {
          // Fetch clients for specific agent
          response = await clientsService.getClientsByAgent(agentSlug);
        } else {
          // Fetch all clients
          response = await clientsService.getAllClients();
        }

        if (response.status && response.data) {
          const data = response.data;

          if (Array.isArray(data)) {
            clientsArray = data;
          } else if (typeof data === 'object' && data !== null) {
            // Handle object with numeric keys (like { "0": {...}, "1": {...} })
            if (Object.keys(data).some(key => !isNaN(key))) {
              clientsArray = Object.values(data);
            } else if (data?.data) {
              if (Array.isArray(data.data)) {
                clientsArray = data.data;
              } else if (data.data?.data && Array.isArray(data.data.data)) {
                clientsArray = data.data.data;
              } else if (typeof data.data === 'object' && data.data !== null) {
                // Handle object with numeric keys in data.data
                if (Object.keys(data.data).some(key => !isNaN(key))) {
                  clientsArray = Object.values(data.data);
                }
              }
            } else if (data?.status_code === 200 && data?.data) {
              if (Array.isArray(data.data)) {
                clientsArray = data.data;
              } else if (data.data?.data && Array.isArray(data.data.data)) {
                clientsArray = data.data.data;
              } else if (typeof data.data === 'object' && data.data !== null) {
                // Handle object with numeric keys
                if (Object.keys(data.data).some(key => !isNaN(key))) {
                  clientsArray = Object.values(data.data);
                }
              }
            }
          }

          const mappedClients = clientsArray.map(mapClientFromApi);
          setClients(mappedClients);

          // Extract agent info from first client if viewing agent-specific clients
          if (agentSlug && mappedClients.length > 0 && mappedClients[0].agentName) {
            setAgentInfo({
              name: mappedClients[0].agentName,
              code: mappedClients[0].agentCode,
              email: mappedClients[0].agentEmail,
              phone: mappedClients[0].agentPhone,
            });
          }
        } else {
          toast.error(response.message || "Failed to fetch clients");
          setClients([]);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            "An error occurred while fetching clients"
        );
        setClients([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [agentSlug]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      let response;
      let clientsArray = [];

      if (agentSlug) {
        response = await clientsService.getClientsByAgent(agentSlug);
      } else {
        response = await clientsService.getAllClients();
      }

      if (response.status && response.data) {
        const data = response.data;

        if (Array.isArray(data)) {
          clientsArray = data;
        } else if (typeof data === 'object' && data !== null) {
          if (Object.keys(data).some(key => !isNaN(key))) {
            clientsArray = Object.values(data);
          } else if (data?.data) {
            if (Array.isArray(data.data)) {
              clientsArray = data.data;
            } else if (data.data?.data && Array.isArray(data.data.data)) {
              clientsArray = data.data.data;
            } else if (typeof data.data === 'object' && data.data !== null) {
              if (Object.keys(data.data).some(key => !isNaN(key))) {
                clientsArray = Object.values(data.data);
              }
            }
          } else if (data?.status_code === 200 && data?.data) {
            if (Array.isArray(data.data)) {
              clientsArray = data.data;
            } else if (data.data?.data && Array.isArray(data.data.data)) {
              clientsArray = data.data.data;
            } else if (typeof data.data === 'object' && data.data !== null) {
              if (Object.keys(data.data).some(key => !isNaN(key))) {
                clientsArray = Object.values(data.data);
              }
            }
          }
        }

        const mappedClients = clientsArray.map(mapClientFromApi);
        setClients(mappedClients);

        if (agentSlug && mappedClients.length > 0 && mappedClients[0].agentName) {
          setAgentInfo({
            name: mappedClients[0].agentName,
            code: mappedClients[0].agentCode,
            email: mappedClients[0].agentEmail,
            phone: mappedClients[0].agentPhone,
          });
        }

        toast.success("Clients data refreshed successfully");
      } else {
        toast.error(response.message || "Failed to refresh clients");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "An error occurred while refreshing clients"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedClient(null);
  };

  // Activation/Deactivation handlers - COMMENTED OUT
  // const handleActivateClient = (client) => {
  //   setClientToActivate(client);
  //   setIsDeactivating(client.status?.toLowerCase() === "active");
  //   setShowActivateModal(true);
  // };

  // const closeActivateModal = () => {
  //   setShowActivateModal(false);
  //   setClientToActivate(null);
  //   setActivating(false);
  //   setIsDeactivating(false);
  // };

  const handleEditClient = (client) => {
    setClientToEdit(client);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setClientToEdit(null);
  };

  const handleClientUpdated = () => {
    handleRefresh();
    closeEditModal();
  };

  // Activation handler - COMMENTED OUT
  // const handleClientActivated = () => {
  //   handleRefresh();
  //   closeActivateModal();
  // };

  if (loading && clients.length === 0) {
    return <Loader fullScreen={true} text="Loading clients..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-3 py-4 space-y-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8 sm:space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex-shrink-0">
            {agentSlug && agentInfo ? (
              <>
                <div className="flex items-center mb-2">
                  <button
                    onClick={() => navigate("/admin/agents")}
                    className="p-2 mr-3 text-gray-600 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
                    title="Back to Agents"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h1 className="flex items-center text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl xl:text-4xl">
                    <UserCheck className="flex-shrink-0 mr-2 w-6 h-6 text-emerald-600 sm:w-7 sm:h-7 lg:w-8 lg:h-8 sm:mr-3" />
                    <span className="truncate">Registered Clients</span>
                  </h1>
                </div>
                <p className="mt-1 text-sm text-gray-600 sm:text-base sm:mt-2">
                  Clients registered by{" "}
                  <span className="font-semibold text-emerald-600">
                    {agentInfo.name}
                  </span>
                  {agentInfo.code && (
                    <span className="px-2 py-1 ml-2 font-mono text-xs text-emerald-800 bg-emerald-100 rounded">
                      {agentInfo.code}
                    </span>
                  )}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center mb-2">
                  <button
                    onClick={() => navigate(-1)}
                    className="p-2 mr-3 text-gray-600 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
                    title="Go Back"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h1 className="flex items-center text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl xl:text-4xl">
                    <UserCheck className="flex-shrink-0 mr-2 w-6 h-6 text-emerald-600 sm:w-7 sm:h-7 lg:w-8 lg:h-8 sm:mr-3" />
                    <span className="truncate">Registered Clients</span>
                  </h1>
                </div>
                <p className="mt-1 text-sm text-gray-600 sm:text-base sm:mt-2">
                  Manage and monitor all registered clients
                </p>
              </>
            )}
          </div>

          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleRefresh}
              className="bg-gray-100 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <RefreshCw className="flex-shrink-0 mr-2 w-4 h-4" />
              <span className="hidden xs:inline">Refresh</span>
              <span className="xs:hidden">Reload</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <ClientStatsCards clients={clients} />

        {/* Filters */}
        <ClientFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          regionFilter={regionFilter}
          setRegionFilter={setRegionFilter}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          showMobileFilters={showMobileFilters}
          setShowMobileFilters={setShowMobileFilters}
        />

        {/* Clients Display */}
        <div className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm sm:rounded-xl">
          <div className="px-3 py-3 bg-gray-50 border-b border-gray-200 sm:px-4 lg:px-6 sm:py-4">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
                Client List ({filteredClients.length}{" "}
                {filteredClients.length === 1 ? "client" : "clients"} found)
              </h3>
              <div className="hidden text-xs text-gray-500 sm:block sm:text-sm">
                Showing {filteredClients.length} of {clients.length} clients
              </div>
            </div>
          </div>

          {/* Desktop View */}
          <ClientTable
            clients={filteredClients}
            onView={handleViewClient}
            onEdit={handleEditClient}
            // onActivate={handleActivateClient} // COMMENTED OUT
          />

          {/* Mobile Card View */}
          <ClientCard
            clients={filteredClients}
            onView={handleViewClient}
            onEdit={handleEditClient}
            // onActivate={handleActivateClient} // COMMENTED OUT
          />

          {/* Empty State */}
          {filteredClients.length === 0 && !loading && (
            <div className="py-12 text-center lg:py-16">
              <UserCheck className="mx-auto w-12 h-12 text-gray-300 lg:h-16 lg:w-16" />
              <h3 className="mt-4 text-base font-medium text-gray-900 lg:text-lg">
                No clients found
              </h3>
              <p className="px-4 mx-auto mt-2 max-w-sm text-sm text-gray-500 lg:text-sm">
                {searchTerm || statusFilter !== "all" || regionFilter !== "all"
                  ? "Try adjusting your search or filter criteria to find clients."
                  : "No clients have been registered yet."}
              </p>
            </div>
          )}
        </div>

        {/* Mobile Stats */}
        <div className="text-center lg:hidden">
          <div className="inline-block px-3 py-2 text-xs text-gray-500 bg-gray-100 rounded-lg">
            Showing {filteredClients.length} of {clients.length} clients
          </div>
        </div>

        {/* Modals */}
        {showViewModal && selectedClient && (
          <ClientViewModal
            client={selectedClient}
            onClose={closeViewModal}
          />
        )}

        {showEditModal && clientToEdit && (
          <ClientEditModal
            client={clientToEdit}
            onClose={closeEditModal}
            onSuccess={handleClientUpdated}
          />
        )}

        {/* Activation Modal - COMMENTED OUT */}
        {/* {showActivateModal && clientToActivate && (
          <ClientActivateModal
            client={clientToActivate}
            isDeactivating={isDeactivating}
            activating={activating}
            setActivating={setActivating}
            onClose={closeActivateModal}
            onSuccess={handleClientActivated}
          />
        )} */}
      </div>
    </div>
  );
};

export default RegisteredClients;
